import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { isEphemeralImageUrl, cacheImageIfNeeded } from '@/lib/imageUtils';

interface SavedImageInfo {
  imageUrl: string;
  savedItemId: string | null;
}

export function useSaveImage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [savedImages, setSavedImages] = useState<Map<string, string>>(new Map());
  const [isLoading, setIsLoading] = useState(false);

  // Check if images are already saved
  const checkSavedStatus = useCallback(async (imageUrls: string[]) => {
    if (!user || imageUrls.length === 0) return;

    try {
      const { data, error } = await supabase
        .from('saved_items')
        .select('id, page_id, image_url, flux_pages:page_id(data)')
        .eq('user_id', user.id);

      if (error) throw error;

      const newMap = new Map<string, string>();
      data?.forEach((item: any) => {
        const fp = item.flux_pages;
        const d = fp?.data;
        const resolvedImg = (item.page_id && d && typeof d === 'object')
          ? (d.cdnUrls?.[0] || d.imageUrls?.[0] || item.image_url || null)
          : (item.image_url ?? null);
        if (resolvedImg && imageUrls.includes(resolvedImg)) {
          newMap.set(resolvedImg, item.id);
        }
      });
      setSavedImages(prev => {
        const merged = new Map(prev);
        newMap.forEach((value, key) => merged.set(key, value));
        return merged;
      });
    } catch (error) {
      console.error('Error checking saved status:', error);
    }
  }, [user]);

  const saveImage = async (imageUrl: string, name?: string): Promise<boolean> => {
    if (!user) {
      toast.error('Please sign in to save items');
      return false;
    }

    setIsLoading(true);
    try {
      // Cache ephemeral Google Shopping thumbnails before saving
      let permanentUrl = imageUrl;
      if (isEphemeralImageUrl(imageUrl)) {
        permanentUrl = await cacheImageIfNeeded(imageUrl);
      }

      const { data, error } = await supabase
        .from('saved_items')
        .insert({
          user_id: user.id,
          source_type: 'image',
          image_url: permanentUrl,
          name: name || 'Saved from poll',
        } as any)
        .select('id')
        .single();

      if (error) throw error;

      setSavedImages(prev => new Map(prev).set(imageUrl, data.id));
      queryClient.invalidateQueries({ queryKey: ['saved-items', user.id] });
      return true;
    } catch (error) {
      console.error('Error saving image:', error);
      toast.error('Failed to save item');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const unsaveImage = async (imageUrl: string): Promise<boolean> => {
    if (!user) return false;

    const savedItemId = savedImages.get(imageUrl);
    if (!savedItemId) return false;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('saved_items')
        .delete()
        .eq('id', savedItemId)
        .eq('user_id', user.id);

      if (error) throw error;

      setSavedImages(prev => {
        const newMap = new Map(prev);
        newMap.delete(imageUrl);
        return newMap;
      });
      queryClient.invalidateQueries({ queryKey: ['saved-items', user.id] });
      return true;
    } catch (error) {
      console.error('Error removing saved item:', error);
      toast.error('Failed to remove item');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSave = async (imageUrl: string, name?: string): Promise<boolean> => {
    const isSaved = savedImages.has(imageUrl);
    if (isSaved) {
      return unsaveImage(imageUrl);
    } else {
      return saveImage(imageUrl, name);
    }
  };

  const isSaved = (imageUrl: string): boolean => {
    return savedImages.has(imageUrl);
  };

  return {
    saveImage,
    unsaveImage,
    toggleSave,
    isSaved,
    checkSavedStatus,
    isLoading,
  };
}
